import moment from 'moment';
import {AttrsSchema, Component, ConfigOptions} from 'panel';
import debounce from 'lodash/debounce';

import {reporter} from 'common/report/error-reporting';
import {trackVisitedReport, VisitedReportReason} from 'common/util/server-tracking';
import {TableColumn} from 'common/components/table/types';
import {OptionsBarOption, OptionsType} from 'common/widgets/load-entity-modal/options-bar/types';
import {
  addRecentItem,
  DEFAULT_SEARCH_QUERY_STRING,
  executeSearch,
  getDisplayContext,
  getSearchIconFromDisplayContext,
} from 'common/report/util/search';
import {
  RecentSearchItem,
  RecentSearchItemType,
  SearchDisplayContext,
  SearchDisplayContextDisplayName,
  SearchQueryFilters,
  SearchQuerySort,
  SearchSortKey,
  SearchSortOption,
  SearchSortOrder,
} from 'common/report/util/search/types';
import {
  ContentDiscoveryType,
  getSearchCommonTrackingProperties,
  searchEventPrefix,
  SearchSource,
  SearchTrackedEvent,
  SearchTrackedProperty,
} from 'common/report/util/search/tracking';
import {formatNumber} from 'common/util';
import {emitTrackEvent} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {findKeyByValue} from 'common/util/typescript';
import {Visibility} from 'common/components/base/stackable';
import newTableStyle from 'common/components/table/v2.styl?inline';
import {userDetailsStore} from 'common/report/stores/user-details';
import {userColumnProps} from 'common/widgets/table-column/user';

import template from './index.jade';
import css from './index.styl?inline';
import {LoadEntityModalAttrs, LoadEntityModalState, OwnerFilter, RowEntity, Selection, TableField} from './types';
import {CAPTURED_TRACKING} from './tracking';

export class LoadEntityModal extends Component<LoadEntityModalState, LoadEntityModalAttrs> {
  private optionsBar: any;
  private userDetailsStoreCallback: () => void;

  static get attrsSchema(): AttrsSchema<LoadEntityModalAttrs> {
    return {
      ...super.attrsSchema,
      'project-id': `string`,
      'track-name': `string`,
      'entities-loading': `boolean`,
      'empty-message': {
        type: `string`,
        description: `empty message shown in table`,
        default: `no item found.`,
      },
      'modal-title': {type: `string`},
      open: {type: `boolean`},
      'display-context': {
        type: `string`,
        enum: Object.values(SearchDisplayContext),
        description: `context we're seeing this in, such as insights, funnels, etc`,
      },
      'search-text': `string`,
      'user-id': {type: `number`},
      'workspace-id': `number`,
      'default-sort-option': {
        type: `string`,
        description: `sort option to override default`,
        tsType: `SearchSortOption`,
      },
    };
  }

  private triggerCloseModal() {
    const {results, searchedText} = this.state;
    this.track(SearchTrackedEvent.ClosedModal, {
      ...this.getCommonTrackingProperties(),
      [SearchTrackedProperty.ResultAmount]: results.length,
      [SearchTrackedProperty.SearchQuery]: searchedText,
    });
    this.dispatchEvent(new CustomEvent(`close`));
  }

  private track(event: string, props: Obj = {}) {
    if (!this.attr(`track-name`)) {
      return;
    }

    if (CAPTURED_TRACKING.has(event)) {
      emitTrackEvent(this, {
        eventName: event,
        eventPrefix: searchEventPrefix,
        props: {
          ...props,
          project_id: this.attr(`project-id`),
          tab: this.attr(`track-name`),
          source: `mp-load-entity-modal`,
        },
        cancelable: true,
      });
    }
  }

  get config(): ConfigOptions<LoadEntityModalState> {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        errored: false,
        results: [],
        isResultsLoading: false,
        lastSearchedText: DEFAULT_SEARCH_QUERY_STRING,
        name: ``,
        open: false,
        requestIndex: 0,
        searchedText: DEFAULT_SEARCH_QUERY_STRING,
        selection: {
          owner: [],
          report: [],
          sort: SearchSortOption.Default,
        },
        shortenedTable: false,
      },
      helpers: {
        SearchDisplayContext,
        getLoadModalAttributes: () => ({
          closeable: true,
          open: this.attr(`open`),
          'grow-to-content': true,
          elref: `loadModal`,
        }),

        optionsBarInserted: (vnode) => (this.optionsBar = vnode.elm),

        handleLoadModalStateChange: (ev) => {
          const {state} = ev.detail;
          if (state === Visibility.Closed) {
            this.triggerCloseModal();
          } else if (state === Visibility.Open) {
            requestAnimationFrame(() => this.optionsBar?.focus());
          }
        },

        onRowSelect: (ev) => {
          ev.stopPropagation();
          const {results, searchedText} = this.state;
          const row: RowEntity = ev.detail.row;
          const trackingProps = {
            ...this.getCommonTrackingProperties(),
            [SearchTrackedProperty.IsOwner]: row.userId === this.attr(`user-id`),
            [SearchTrackedProperty.Owner]: row.user,
            [SearchTrackedProperty.PopularityScore]: row.popularityScore,
            [SearchTrackedProperty.FinalScore]: row.finalScore,
            [SearchTrackedProperty.ReportDescription]: row.description,
            [SearchTrackedProperty.ReportTitle]: row.name,
            [SearchTrackedProperty.ResultPosition]: this.getResultPosition(row.value.id),
            [SearchTrackedProperty.ResultAmount]: results.length,
            [SearchTrackedProperty.ResultLink]: row.link,
            [SearchTrackedProperty.SearchQuery]: searchedText,
          };

          addRecentItem({
            description: row.description,
            icon: getSearchIconFromDisplayContext(row.displayContext),
            isOwner: row.userId === this.attr(`user-id`),
            key: `${RecentSearchItemType.Entity}-${row.value.id}`,
            link: row.link,
            label: row.name,
            subLabel: `${row.user || ``} · ${row.lastModifiedDisplay}`,
            type: RecentSearchItemType.Entity,
            user: row.user || ``,
          } as RecentSearchItem);

          const sameReportReadyNewSearch =
            !this.attr(`entities-loading`) && row.displayContext === this.attr(`display-context`);
          const shouldUseNewTab = ev.detail.modifierKeys.ctrl || ev.detail.modifierKeys.meta;
          const url = row.link;
          this.track(SearchTrackedEvent.SelectReport, trackingProps);

          if (shouldUseNewTab) {
            window.open(url, `_blank`);
          } else if (sameReportReadyNewSearch) {
            this.dispatchEvent(
              new CustomEvent(`select`, {
                detail: {entity: {...row, trackingProps}},
              }),
            );
            trackVisitedReport({
              reportName: this.attr(`track-name`),
              projectId: this.attr(`project-id`),
              reason: VisitedReportReason.BookmarkSelect,
              selectedBookmark: true,
            });
            this.triggerCloseModal();
          } else {
            window.location.assign(url);
            this.triggerCloseModal();
          }
        },

        trackTableEvents: (ev) => {
          ev.stopPropagation();
          this.track(ev.detail.eventName, ev.detail.props);
        },

        getFilterOptions: (): OptionsBarOption[] => {
          const {searchedText, selection} = this.state;
          return [
            {
              name: `Sort`,
              multiple: false,
              showNumItems: false,
              icon: `sort`,
              menuWidth: 110,
              type: OptionsType.Select,
              hidden: searchedText !== DEFAULT_SEARCH_QUERY_STRING,
              sections: [
                {
                  items: Object.values(SearchSortOption)
                    .filter((item) => item !== SearchSortOption.Default)
                    .map((item) => ({label: item, value: item})),
                },
              ],
            },
            {
              name: `Filter`,
              multiple: true,
              showNumItems: true,
              icon: `filter`,
              menuWidth: 180,
              type: OptionsType.Sidebar,
              sections: [
                {
                  label: `Ownership`,
                  items: Object.values(OwnerFilter).map((value) => ({
                    label: value,
                    value,
                    isSelected: selection.owner.includes(value),
                    sectionName: Selection.Owner,
                  })),
                },
                {
                  label: `Types`,
                  items: Object.values(SearchDisplayContext).map((value) => ({
                    label: SearchDisplayContextDisplayName[findKeyByValue(SearchDisplayContext, value)],
                    icon: getSearchIconFromDisplayContext(value),
                    value,
                    isSelected: selection.report.includes(value),
                    sectionName: Selection.Report,
                  })),
                },
              ],
            },
          ];
        },
        handleOptionSelected: ({detail}) => {
          const {isSelected, value, sectionName} = detail;
          const applyFilter = (selection, value, isSelected) => {
            let filteredSelection = selection;
            if (isSelected) {
              filteredSelection = filteredSelection.filter((filter) => filter !== value);
            } else {
              filteredSelection.push(value);
            }

            return filteredSelection;
          };

          let update = {};
          if (sectionName === Selection.Report) {
            update = {report: applyFilter(this.state.selection.report, value, isSelected)};
            this.track(SearchTrackedEvent.UpdateFilter, {
              ...this.getCommonTrackingProperties(),
              [SearchTrackedProperty.ReportFilters]: update[`report`],
            });
          } else if (sectionName === Selection.Owner) {
            update = {owner: applyFilter(this.state.selection.owner, value, isSelected)};
            this.track(SearchTrackedEvent.UpdateFilter, {
              ...this.getCommonTrackingProperties(),
              [SearchTrackedProperty.OwnerFilters]: update[`owner`],
            });
          }

          if (Object.values(SearchSortOption).includes(value)) {
            update = {sort: value};
            this.track(SearchTrackedEvent.Sort, {
              ...this.getCommonTrackingProperties(),
              [SearchTrackedProperty.SortValue]: value,
            });
          }

          this.update({
            selection: {
              ...this.state.selection,
              ...update,
            },
          });

          this.executeSearch();
        },
        handleNewSearchText: ({detail}) => {
          if (detail.track) {
            this.track(SearchTrackedEvent.RecentQuerySelected, {
              ...this.getCommonTrackingProperties(),
              [SearchTrackedProperty.SearchQuery]: detail.value,
            });
          }
          this.update({
            searchedText: detail.value,
          });
          this.executeSearch();
        },
        handleSubmitSearch: () => {
          const {searchedText} = this.state;
          const eventName = searchedText ? SearchTrackedEvent.EditSearchQuery : SearchTrackedEvent.ResetSearchQuery;
          // track when a user hits "enter"
          this.track(eventName, {
            ...this.getCommonTrackingProperties(),
            [SearchTrackedProperty.SearchQuery]: searchedText,
          });
          this.executeSearch();
        },
        handleSlideChange: (ev) => this.update({shortenedTable: ev.detail.open}),
        handleResetSections: () => this.update({selection: {...this.state.selection, owner: [], report: []}}),
        getNewTableColumns: (mixins) => this.getNewTableColumns(mixins),
        getRealSearchRows: () => this.getNewTableRows(this.getSections()[0].items),
        getNewTableAttrs: () => this.getNewTableAttrs(),
        getEntityDisplayType: (entity: RowEntity) =>
          SearchDisplayContextDisplayName[findKeyByValue(SearchDisplayContext, entity.displayContext)],
        getEntityIcon: (entity: RowEntity) => getSearchIconFromDisplayContext(entity.displayContext),
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.update({
      isResultsLoading: true,
      searchedText: this.attr(`search-text`),
      selection: {
        ...this.state.selection,
        report: !this.attr(`display-context`) ? [] : [this.attr(`display-context`)],
        sort: (this.attr(`default-sort-option`) as SearchSortOption) || SearchSortOption.Default,
      },
    });
    this.executeSearch();

    if (!this.attr(`display-context`)) {
      this.track(SearchTrackedEvent.OpenModal, this.getCommonTrackingProperties());
    }

    this.userDetailsStoreCallback = () => this.update();
    userDetailsStore.subscribe(this.userDetailsStoreCallback);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    userDetailsStore.unsubscribe(this.userDetailsStoreCallback);
  }

  getNewTableAttrs() {
    const {isResultsLoading} = this.state;

    return {
      loading: isResultsLoading,
      'default-sort-field': ``,
      'empty-message': this.attr(`empty-message`),
      'sticky-titles': true,
      'disable-page-sticky-titles': true,
      'style-override': newTableStyle,
      'track-name': this.attr(`track-name`),
      'max-body-height': `450px`,
      'search-placeholder': `Search for a report or name...`,
      elref: `entityTable`,
    };
  }

  getNewTableColumns(mixins): TableColumn[] {
    const {selection, shortenedTable} = this.state;
    const nameLabel = selection.sort === SearchSortOption.Default ? `Name` : selection.sort;
    let ownerLabel = `Created by Anyone`;
    if (selection.owner.length > 0 && selection.owner.length < Object.values(OwnerFilter).length) {
      const ownerSet = new Set(selection.owner);

      if (ownerSet.has(OwnerFilter.Shared) && !ownerSet.has(OwnerFilter.Owner)) {
        ownerLabel = `Created by Others`;
      } else if (ownerSet.has(OwnerFilter.Owner) && !ownerSet.has(OwnerFilter.Shared)) {
        ownerLabel = `Created by You`;
      }
    }
    const isExploreOnly = selection.report.length === 1 && selection.report[0] === SearchDisplayContext.Explore;

    return [
      {
        field: TableField.Name,
        title: nameLabel,
        flexGrow: `2`,
        flexBasis: `180px`,
        isSearchable: true,
        isSortable: false,
        cellRenderer: mixins[`new-modal-cell-entity-name-renderer`],
      },
      {
        field: TableField.User,
        title: ownerLabel,
        flexGrow: `1`,
        flexBasis: `100px`,
        isSearchable: true,
        isSortable: false,
        ...userColumnProps,
      },
      {
        field: TableField.LastModified,
        title: `Last Modified`,
        flexBasis: shortenedTable ? `100px` : `150px`,
        isSearchable: true,
        isSortable: false,
        labelTransform: (entity) => entity.lastModifiedDisplay,
      },
      ...(isExploreOnly
        ? []
        : [
            {
              field: TableField.Count,
              titleIcon: `person`,
              titleTooltip: `Unique views (30 days)`,
              flexBasis: `52px`,
              isSearchable: true,
              isSortable: false,
              labelTransform: (entity) =>
                entity.displayContext === SearchDisplayContext.Explore ? `-` : formatNumber(entity.uniqueCount || 0),
            },
          ]),
    ];
  }

  getNewTableRows(items): RowEntity[] {
    if (!items || !items.length) {
      return [];
    }

    return items;
  }

  // TODO @tiffanyqi eventually will house multiple sections of top results and recent results
  getSections() {
    const {results, isResultsLoading} = this.state;

    if (isResultsLoading) {
      return [
        {
          loading: true,
          items: [],
        },
      ];
    }

    return [
      {
        label: `Results`,
        loading: false,
        items: results.map(
          (item): RowEntity => {
            const lastEditedMoment = moment(item.last_edited);
            return {
              rowType: item.type,
              name: item.name,
              description: item.description,
              displayContext: getDisplayContext(item),
              editable: item.can_update_basic,
              lastModified: item.last_edited,
              lastModifiedDisplay: lastEditedMoment.format(`MMM DD, YYYY`),
              link: item.link,
              popularityScore: item.scores.popularity,
              finalScore: item.scores.final,
              report: item.report,
              uniqueCount: item.unique_count,
              user: item.user,
              userId: item.user_id,
              userEmail: item.email,
              // to make bookmark deletion and selection work
              value: {
                id: item.id,
                title: item.name,
                userId: item.user_id,
                rowType: item.type,
              },
            };
          },
        ),
      },
    ];
  }

  public getResultPosition(entityId): number {
    return this.state.results.findIndex((entity) => entity.id === entityId);
  }

  public executeSearch = debounce(async () => {
    const currentRequestIndex = this.state.requestIndex + 1;
    const {searchedText, selection} = this.state;
    this.update({
      isResultsLoading: true,
      lastSearchedText: searchedText,
      requestIndex: currentRequestIndex,
    });

    const queryFilters = this.getQueryFilters();
    let querySort: SearchQuerySort = null;

    if (searchedText === DEFAULT_SEARCH_QUERY_STRING) {
      querySort = this.getQuerySortParams(selection.sort);
    } else {
      // remove sort options if we have a query search term
      this.update({selection: {...this.state.selection, sort: SearchSortOption.Default}});
    }

    const trackingProps = {
      ...this.getCommonTrackingProperties(),
      [SearchTrackedProperty.OwnerFilters]: selection.owner,
      [SearchTrackedProperty.ReportFilters]: selection.report,
      [SearchTrackedProperty.SearchQuery]: searchedText,
      [SearchTrackedProperty.SortValue]: this.state.selection.sort,
    };

    try {
      // always force empty queries to hit the search endpoint for latest results
      const results = await executeSearch({queryFilters, querySort, searchedText, fromFetchCache: !!searchedText});
      if (currentRequestIndex === this.state.requestIndex && results) {
        this.update({
          errored: false,
          results,
          isResultsLoading: false,
        });
        // add recent only if you have results successfully rather than adding each letter
        if (searchedText !== DEFAULT_SEARCH_QUERY_STRING) {
          addRecentItem({
            icon: `search`,
            key: `${RecentSearchItemType.SearchTerm}-${searchedText}`,
            label: searchedText,
            type: RecentSearchItemType.SearchTerm,
          } as RecentSearchItem);
        }
        this.track(SearchTrackedEvent.SearchResultLoaded, {
          ...trackingProps,
          [SearchTrackedProperty.ResultAmount]: results?.length,
        });
      }
      this.track(SearchTrackedEvent.SearchResultLoaded, {
        ...trackingProps,
        [SearchTrackedProperty.ResultAmount]: results?.length,
        [SearchTrackedProperty.ResultsWithPopularity]: results?.filter((item) => item.scores.popularity > 0).length,
      });
    } catch (err) {
      reporter.error(err);
      this.update({errored: true});
      this.track(SearchTrackedEvent.SearchErrored, trackingProps);
    }
  }, 200);

  getQuerySortParams(sort: SearchSortOption) {
    switch (sort) {
      case SearchSortOption.Popularity:
        return {
          key: SearchSortKey.Popularity,
          order: SearchSortOrder.Descending,
        };
      case SearchSortOption.LastModified:
        return {
          key: SearchSortKey.LastModified,
          order: SearchSortOrder.Descending,
        };
      case SearchSortOption.TitleAscending:
        return {
          key: SearchSortKey.Name,
          order: SearchSortOrder.Ascending,
        };
      case SearchSortOption.TitleDescending:
        return {
          key: SearchSortKey.Name,
          order: SearchSortOrder.Descending,
        };
      default:
        return null;
    }
  }

  getQueryFilters(): SearchQueryFilters {
    const {selection} = this.state;
    const queryTypes = selection.report?.length ? selection.report : Object.values(SearchDisplayContext);
    const queryTypeFilters = queryTypes.map((value) => ({value, negated: false}));
    const ownerSet = new Set(selection.owner);
    const queryUserFilters = [];

    if (ownerSet.has(OwnerFilter.Shared) && !ownerSet.has(OwnerFilter.Owner)) {
      queryUserFilters.push({
        value: `me`,
        negated: true,
      });
    }

    if (ownerSet.has(OwnerFilter.Owner) && !ownerSet.has(OwnerFilter.Shared)) {
      queryUserFilters.push({
        value: `me`,
        negated: false,
      });
    }

    return {
      email: queryUserFilters,
      type: queryTypeFilters,
    };
  }

  private getCommonTrackingProperties() {
    return {
      ...getSearchCommonTrackingProperties(),
      [SearchTrackedProperty.ContentDiscoveryType]: this.state.searchedText
        ? ContentDiscoveryType.SearchQuery
        : ContentDiscoveryType.Browse,
      [SearchTrackedProperty.SortValue]: this.state.selection.sort,
      [SearchTrackedProperty.Source]: SearchSource.Modal,
    };
  }
}

export const LoadEntityModalEl = defineAndMakeJsxFactory(`mp-load-entity-modal`, LoadEntityModal);
